import { NetRandom } from '../../core/rng/NetRandom.js';
import { SpawnPositionMode, type CompiledChart } from '../../songpack/index.js';

/**
 * Decides where each wave opens, porting Unity's `ChartScheduler.ResolveOrigin`.
 *
 * A Song Pack stores `randomSeedOffset`, not coordinates, so the position is
 * *derived* — which means the generator has to agree between runtimes or the same
 * chart lays out differently in each. `NetRandom` reproduces .NET's seeded
 * `System.Random` bit for bit (verified against values printed by Unity itself),
 * so the draw sequence here is the draw sequence there. The remaining arithmetic
 * runs at double precision where Unity used `float`, so coordinates agree to
 * float precision rather than to the bit.
 *
 * Structure preserved from the original, because each part carries meaning:
 *
 * - a **fresh generator per event**, seeded `chart.randomSeed + randomSeedOffset`,
 *   so an event's position depends only on the chart and its own offset — never
 *   on how many events preceded it
 * - **rerolling** up to `positionRerollLimit` times, since a candidate can be
 *   rejected for landing outside the field, too near the player, or too near the
 *   previous wave
 * - **no rerolling for deterministic modes**, which would otherwise spin the loop
 *   producing the same rejected point every time
 * - a **fallback** on the opposite side of the player when every attempt fails
 */

export interface Vector2Like {
  readonly x: number;
  readonly y: number;
}

export interface SpawnResolverConfig {
  /**
   * Radius of the play area, in world units.
   *
   * Unity passed `playAreaRadius - playerCollisionRadius` (7.75 with its default
   * PlayerSettings). Those settings are not part of Song Pack v1, so this is the
   * web runtime's own field radius; it is the one number here that is
   * intentionally not Unity's.
   */
  readonly fieldRadius: number;
  /**
   * Where the player is assumed to be.
   *
   * Only `PlayerPositionAtSpawn` and the `minimumDistanceFromPlayer` rejection
   * read it. There is no player until Phase 3, so this defaults to Unity's
   * starting position rather than the origin.
   */
  readonly playerPosition: Vector2Like;
}

export const DEFAULT_PLAYER_POSITION: Vector2Like = Object.freeze({ x: 0, y: -4 });

export class SpawnResolver {
  private previousX = 0;
  private previousY = 0;
  private hasPrevious = false;

  constructor(
    private readonly chart: CompiledChart,
    private readonly config: SpawnResolverConfig,
  ) {}

  /** Clears the "previous origin" memory. Used when a song restarts. */
  reset(): void {
    this.hasPrevious = false;
    this.previousX = 0;
    this.previousY = 0;
  }

  /**
   * Resolves the origin for one chart event and remembers it as the previous
   * origin for the next call, exactly as the Unity scheduler does.
   *
   * @param out Written in place so a spawn allocates nothing.
   */
  resolve(eventIndex: number, out: { x: number; y: number }): void {
    const events = this.chart.events;
    const radius = this.config.fieldRadius;
    const player = this.config.playerPosition;

    const seedOffset = events.randomSeedOffset[eventIndex] ?? 0;
    const random = new NetRandom(this.chart.runtime.randomSeed + seedOffset);
    const limit = Math.max(1, this.chart.runtime.positionRerollLimit);
    const previousMin = this.chart.runtime.minimumDistanceFromPreviousOrigin;

    const mode = events.spawnPositionMode[eventIndex] ?? SpawnPositionMode.FixedWorldPosition;
    const edgePadding = events.edgePadding[eventIndex] ?? 0;
    const minimumFromPlayer = events.minimumDistanceFromPlayer[eventIndex] ?? 0;

    let candidateX = 0;
    let candidateY = 0;

    for (let attempt = 0; attempt < limit; attempt += 1) {
      switch (mode) {
        case SpawnPositionMode.NormalizedPosition: {
          candidateX = lerp(-radius, radius, clamp01(events.normalizedPositionX[eventIndex] ?? 0));
          candidateY = lerp(-radius, radius, clamp01(events.normalizedPositionY[eventIndex] ?? 0));
          break;
        }
        case SpawnPositionMode.RandomInsidePlayArea: {
          const angle = random.nextDouble() * Math.PI * 2;
          // sqrt keeps the distribution uniform over the disc instead of
          // crowding the centre.
          const distance = Math.sqrt(random.nextDouble()) * Math.max(0, radius - edgePadding);
          candidateX = Math.cos(angle) * distance;
          candidateY = Math.sin(angle) * distance;
          break;
        }
        case SpawnPositionMode.RandomOnEdge: {
          // Order matters: Unity draws the side first, then the position along
          // it. Swapping them would consume the same numbers differently.
          const side = random.next(0, 4);
          const along = lerp(-radius, radius, random.nextDouble());
          const edge = Math.max(0, radius - edgePadding);
          if (side === 0) {
            candidateX = -edge;
            candidateY = along;
          } else if (side === 1) {
            candidateX = edge;
            candidateY = along;
          } else if (side === 2) {
            candidateX = along;
            candidateY = edge;
          } else {
            candidateX = along;
            candidateY = -edge;
          }
          break;
        }
        case SpawnPositionMode.PlayerPositionAtSpawn: {
          candidateX = player.x;
          candidateY = player.y;
          break;
        }
        default: {
          const fixedX = events.fixedWorldPositionX[eventIndex] ?? 0;
          const fixedY = events.fixedWorldPositionY[eventIndex] ?? 0;
          const useFixed = fixedX !== 0 || fixedY !== 0;
          candidateX = useFixed ? fixedX : (events.originX[eventIndex] ?? 0);
          candidateY = useFixed ? fixedY : (events.originY[eventIndex] ?? 0);
          break;
        }
      }

      const inside = candidateX * candidateX + candidateY * candidateY <= radius * radius;
      const farFromPlayer =
        distanceTo(candidateX, candidateY, player.x, player.y) >= minimumFromPlayer;
      const farFromPrevious =
        !this.hasPrevious ||
        distanceTo(candidateX, candidateY, this.previousX, this.previousY) >= previousMin;

      if (inside && farFromPlayer && farFromPrevious) {
        this.accept(candidateX, candidateY, out);
        return;
      }

      // Rerolling a mode that ignores the generator would loop on the same point.
      if (
        mode === SpawnPositionMode.FixedWorldPosition ||
        mode === SpawnPositionMode.NormalizedPosition ||
        mode === SpawnPositionMode.PlayerPositionAtSpawn
      ) {
        break;
      }
    }

    // Every attempt was rejected: fall back to the far side of the player.
    const playerLength = Math.hypot(player.x, player.y);
    let fallbackX: number;
    let fallbackY: number;
    if (playerLength > 0) {
      const reach = Math.max(1, radius * 0.75);
      fallbackX = (-player.x / playerLength) * reach;
      fallbackY = (-player.y / playerLength) * reach;
    } else {
      fallbackX = 0;
      fallbackY = 0;
    }
    if (fallbackX * fallbackX + fallbackY * fallbackY < 0.01) {
      fallbackX = 0;
      fallbackY = -radius * 0.75;
    }
    const clamped = clampMagnitude(fallbackX, fallbackY, radius);
    this.accept(clamped.x, clamped.y, out);
  }

  private accept(x: number, y: number, out: { x: number; y: number }): void {
    out.x = x;
    out.y = y;
    this.previousX = x;
    this.previousY = y;
    this.hasPrevious = true;
  }
}

function clamp01(value: number): number {
  return value < 0 ? 0 : value > 1 ? 1 : value;
}

/** Unity's `Mathf.Lerp`, which clamps its interpolant. */
function lerp(from: number, to: number, t: number): number {
  return from + (to - from) * clamp01(t);
}

function distanceTo(ax: number, ay: number, bx: number, by: number): number {
  return Math.hypot(ax - bx, ay - by);
}

function clampMagnitude(x: number, y: number, max: number): { x: number; y: number } {
  const lengthSquared = x * x + y * y;
  if (lengthSquared <= max * max) return { x, y };
  const scale = max / Math.sqrt(lengthSquared);
  return { x: x * scale, y: y * scale };
}
